import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Paper, Box, Typography,
  Stepper, Step, StepLabel, Grid, Button, Alert
} from "@mui/material";
import { motion } from "framer-motion";

import Step1 from "./Steps/Step1";
import Step2 from "./Steps/Step2";
import Step3 from "./Steps/Step3";
import logo from "../assets/logo.png";

const useTTS = (enabled) => {
  const synthRef = useRef(typeof window !== "undefined" ? window.speechSynthesis : null);
  const voicesRef = useRef([]);

  useEffect(() => {
    if (!synthRef.current) return;
    const loadVoices = () => {
      const voices = synthRef.current.getVoices();
      voicesRef.current = voices;
    };
    loadVoices();
    if (typeof window !== "undefined") {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  const speak = (text) => {
    if (!enabled || !synthRef.current || !text) return;
    try {
      synthRef.current.cancel();
      const utter = new SpeechSynthesisUtterance(text);

      const friendly =
        voicesRef.current.find(v => v.name.includes("Google US English Female")) ||
        voicesRef.current.find(v => v.lang.includes("en-US") && v.name.toLowerCase().includes("female")) ||
        voicesRef.current.find(v => v.lang.includes("en-US")) ||
        voicesRef.current[0];

      if (friendly) utter.voice = friendly;

      utter.rate = 1.02;
      utter.pitch = 1.05;
      utter.volume = 1;

      synthRef.current.speak(utter);
    } catch (e) {
      console.error(e);
    }
  };

  const stop = () => { try { synthRef.current?.cancel(); } catch {} };
  return { speak, stop };
};


const SpeechBubble = ({ text }) => (
  <motion.div
    key={text}
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.28 }}
    style={{
      maxWidth: 420,
      background: "rgba(255,255,255,0.95)",
      border: "1px solid #eef1f5",
      borderRadius: 16,
      padding: "10px 14px",
      color: "#2a2a2a",
      boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
    }}
  >
    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{text}</Typography>
  </motion.div>
);

export default function RobotRegister({ robotImage, register, verifyEmail, user, onNavigate }) {
  const [step, setStep] = useState(1);
  const [tempUserId, setTempUserId] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [bubbleText, setBubbleText] = useState("Hi! I'm Robo, I'll help you register. What is your name?");
  const [muted, setMuted] = useState(false);
  const { speak, stop } = useTTS(!muted);

  useEffect(() => {
    let text = "";
    if (step === 1) text = "Welcome! Let's start with your basic info. What is your name?";
    if (step === 2) text = `I sent a code to ${email}. Please type your verification code.`;
    if (step === 3) text = "Almost done! Please complete your profile then submit.";
    setBubbleText(text);
    speak(text);
    return () => stop();
  }, [step]);

  useEffect(() => {
    if (!user) return;
    if (user.role_id === 3) onNavigate?.("/student/dashboard");
    else if (user.role_id === 2) onNavigate?.("/teacher/dashboard");
    else if (user.role_id === 1) onNavigate?.("/admin/dashboard");
    else onNavigate?.("/");
  }, [user]); 

  const steps = useMemo(() => ["Start", "Verify And Finish"], []);

  const handleNextStep = async (data) => {
    setLoading(true);
    setError("");
    try {
      if (step === 1) {
        const response = await register(data);
        setTempUserId(response.tempUserId);
        setEmail(data.email);
        setStep(2);
      } else if (step === 2) {
        const userData = await verifyEmail(data.otp, email);
        if (userData.token) localStorage.setItem("token", userData.token);
        if (userData.role_id === 3) onNavigate?.("/student/dashboard");
        else if (userData.role_id === 2) onNavigate?.("/teacher/dashboard");
        else if (userData.role_id === 1) onNavigate?.("/admin/dashboard");
        else onNavigate?.("/");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong. Please try again.";
      setError(msg);
      setBubbleText(msg);
      speak(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => { if (step > 1) setStep(step - 1); };

  const onFocusHints = {
    name: () => { setBubbleText("Great! Please type your full name."); speak("Great! Please type your full name."); },
    email: () => { setBubbleText("What's your email address? I'll send you a code."); speak("What is your email address? I will send you a code."); },
    phone: () => { setBubbleText("Enter your WhatsApp number including country code."); speak("Enter your WhatsApp number including country code."); },
    grade: () => { setBubbleText("Pick your grade level."); speak("Pick your grade level."); },
    password: () => { setBubbleText("Create a strong password."); speak("Create a strong password."); },
    confirm: () => { setBubbleText("Confirm your password. Both must match."); speak("Confirm your password. Both must match."); },
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #1A73E8 0%, #6A1B9A 100%)",
        p: { xs: 1.5, sm: 3 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 1100,
          borderRadius: 3,
          bgcolor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(8px)",
          overflow: "hidden",
        }}
      >
<Box
  sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pt: 4,
    pb: 2,
    borderBottom: '1px solid #eef1f5',
  }}
>
  <Box
    component="img"
    src={logo}
    alt="Allam School"
    sx={{
      width: 84,
      height: 84,
      borderRadius: '50%',
      mb: 1.5,
      boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
    }}
  />
  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A73E8', lineHeight: 1 }}>
    Allam School
  </Typography>
  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 0.5 }}>
    Empowering Future Leaders
  </Typography>
</Box>

        <Grid container spacing={0}>
          {/* Left panel: robot */}
          <Grid
            item xs={12} md={4}
            sx={{
              bgcolor: "transparent",
              p: { xs: 2, md: 3 },
              borderRight: { md: "1px solid #eef1f5" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >

            <Box sx={{ position: "relative", width: { xs: 220, md: 260 }, height: { xs: 220, md: 260 } }}>
              <motion.img
                src={robotImage}
                alt="Robot"
                style={{ width: "100%", height: "100%", objectFit: "contain", filter: "drop-shadow(0 10px 18px rgba(0,0,0,0.15))" }}
                initial={{ scale: 0.95, rotate: -2, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 120, damping: 12 }}
              />
              <Box sx={{ position: "absolute", left: "50%", bottom: -12, transform: "translateX(-50%)" }}>
                <SpeechBubble text={bubbleText} />
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setMuted(m => !m)}
              >
                {muted ? "🔇 Muted" : "🔊 Speaking"}
              </Button>
              <Button size="small" variant="outlined" onClick={() => stop()}>
                Stop
              </Button>
            </Box>

            <Box sx={{ width: "100%", mt: 4 }}>
              <Stepper activeStep={step - 1} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>
            </Box>
          </Grid>

          <Grid item xs={12} md={8} sx={{ p: { xs: 3, md: 5 } }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <motion.div
              key={step}
              initial={{ opacity: 0, x: step === 1 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              {step === 1 && (
                <Step1
                  onNext={async (data) => { await handleNextStep(data); }}
                  loading={loading}
                  onNameFocus={onFocusHints.name}
                  onEmailFocus={onFocusHints.email}
                  onPhoneFocus={onFocusHints.phone}
                  onGradeFocus={onFocusHints.grade}
                  onPasswordFocus={onFocusHints.password}
                  onConfirmFocus={onFocusHints.confirm}
                />
              )}
              {step === 2 && (
                <Step2
                  onNext={async (data) => { await handleNextStep(data); }}
                  onBack={handleBack}
                  email={email}
                  loading={loading}
                />
              )}
              {step === 3 && (
                <Step3
                  onSubmit={async (data) => { await handleNextStep(data); }}
                  onBack={handleBack}
                  loading={loading}
                />
              )}
            </motion.div>

            <Typography variant="body2" align="center" sx={{ mt: 4 }}>
              Already have an account?{" "}
              <Box component="span" sx={{ color: "primary.main", cursor: "pointer" }}
                   onClick={() => onNavigate?.("/login")}>
                Login
              </Box>
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}
